import api from "../../api/client";
import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Modal,
  message,
  Tabs,
  Table,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Badge,
  Spin,
  Tooltip,
  Statistic,
  Progress,
  Alert,
  Descriptions,
  Divider,
  Typography,
  Popconfirm,
  InputNumber,
} from "antd";
import {
  PlusOutlined,
  HistoryOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  FallOutlined,
  LineChartOutlined,
  TeamOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import config from "../../config";
import { useSelector } from "react-redux";
import ThresholdModal from "../../components/ThresholdModal";

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;

const AnimalGrowthTracker = () => {
  const [loading, setLoading] = useState(false);
  const [animalsData, setAnimalsData] = useState({
    goat: [],
    sheep: [],
    pig: [],
    weaner: [],
  });
  const [activeTab, setActiveTab] = useState("goat");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [animalHistory, setAnimalHistory] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [form] = Form.useForm();
  const [stats, setStats] = useState({
    totalAnimals: 0,
    avgWeight: 0,
    totalGain: 0,
    growthRate: 0,
  });
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);

  const account = useSelector((state) => state.account.user);

  // Animal type configuration
  const animalTypes = {
    goat: {
      name: "Goats",
      icon: "🐐",
      color: "",
      growthTable: "goat_growth_records",
    },
    sheep: {
      name: "Sheep",
      icon: "🐑",
      color: "",
      growthTable: "sheep_growth_records",
    },
    pig: {
      name: "Pigs",
      icon: "🐷",
      color: "",
      growthTable: "pig_growth_records",
    },
    weaner: {
      name: "Weaners",
      icon: "🐖",
      color: "",
      growthTable: "weaner_growth_records",
    },
  };

  // Fetch animals data
  const fetchAnimals = async () => {
    setLoading(true);
    try {
      const results = {};
      for (const [type, config] of Object.entries(animalTypes)) {
        const response = await api.get(`/api/${type}/latest-weights`);
        results[type] = response.data;
      }
      setAnimalsData(results);

      // Calculate overall stats
      const allAnimals = Object.values(results).flat();
      const totalAnimals = allAnimals.length;
      const avgWeight =
        allAnimals.reduce((sum, a) => sum + (a.lastWeight || 0), 0) /
        (totalAnimals || 1);
      setStats({
        totalAnimals,
        avgWeight: avgWeight.toFixed(1),
        totalGain: 0,
        growthRate: 0,
      });
    } catch (error) {
      console.error("Error fetching animals:", error);
      message.error("Failed to load animal data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch available tags for modal
  const fetchAvailableTags = async (type) => {
    setLoadingTags(true);
    try {
      const response = await api.get(`/api/${type}/available`);
      const options = response.data.map((animal) => ({
        value: animal.tagNumber,
        label: `${animal.tagNumber} - ${animal.breed || "Unknown Breed"} (${
          animal.weight || 0
        }kg)`,
        ...animal,
      }));
      setAvailableTags(options);
    } catch (error) {
      console.error("Error fetching tags:", error);
      message.error("Failed to load available animals");
    } finally {
      setLoadingTags(false);
    }
  };

  // Fetch growth history
  const fetchGrowthHistory = async (tagNumber, type) => {
    try {
      const response = await api.get(`/api/growth/${type}/${tagNumber}`);
      setAnimalHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
      message.error("Failed to load growth history");
    }
  };

  useEffect(() => {
    fetchAnimals();
  }, []);

  useEffect(() => {
    if (modalVisible) {
      fetchAvailableTags(activeTab);
      form.resetFields();
    }
  }, [modalVisible, activeTab]);

  // Handle add weight record
  const handleAddWeight = async (values) => {
    if (!values.tagNumber) {
      message.error("Please select an animal");
      return;
    }

    console.log("show values", values);

    try {
      const payload = {
        tagNumber: values.tagNumber,
        weight: values.weight,
        measurementDate: values.measurementDate.format("YYYY-MM-DD"),
        notes: values.notes,
        recorded_by: account.id,
        type: activeTab,
      };

      await api.post(`/api/growth/${activeTab}`, payload);
      message.success("Weight record added successfully");
      setModalVisible(false);
      form.resetFields();
      fetchAnimals();
    } catch (error) {
      console.error("Error adding weight:", error);
      message.error(
        error.response?.data?.error || "Failed to add weight record",
      );
    }
  };

  // Handle view history
  const handleViewHistory = async (animal) => {
    setSelectedAnimal(animal);
    await fetchGrowthHistory(animal.tagNumber, animal.type);
    setHistoryModalVisible(true);
  };

  // Table columns
  const getColumns = (type) => [
    {
      title: "Tag Number",
      dataIndex: "tagNumber",
      key: "tagNumber",
      width: 120,
      fixed: "left",
      render: (text) => <Text>{text}</Text>,
    },
    {
      title: "Last Weight (kg)",
      dataIndex: "lastWeight",
      key: "lastWeight",
      width: 120,
      sorter: (a, b) => (a.lastWeight || 0) - (b.lastWeight || 0),
      render: (weight) => <Text strong>{weight || 0} kg</Text>,
    },
    {
      title: "Last Measurement",
      dataIndex: "lastMeasurement",
      key: "lastMeasurement",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
      sorter: (a, b) =>
        dayjs(a.lastMeasurement).unix() - dayjs(b.lastMeasurement).unix(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "alive" ? "green" : "red"}>
          {status || "alive"}
        </Tag>
      ),
    },
    {
      title: "Market Readiness",
      dataIndex: "marketReadiness",
      key: "marketReadiness",
      width: 100,
      render: (status) => (
        <Tag color={status === "Ready FOR MARKET" ? "green" : "red"}>
          {status || "Not Ready"}
        </Tag>
      ),
    },
    {
      title: "Farm",
      dataIndex: "farm",
      key: "farm",
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View Growth History">
          <Button
            icon={<HistoryOutlined />}
            size="small"
            onClick={() => handleViewHistory(record)}
          />
        </Tooltip>
      ),
    },
  ];

  // History table columns
  const historyColumns = [
    {
      title: "Date",
      dataIndex: "measurement_date",
      key: "date",
      width: 120,
      render: (date) => dayjs(date).format("DD MMM YYYY"),
      sorter: (a, b) =>
        dayjs(a.measurement_date).unix() - dayjs(b.measurement_date).unix(),
    },
    {
      title: "Weight (kg)",
      dataIndex: "weight",
      key: "weight",
      width: 100,
      sorter: (a, b) => (a.weight || 0) - (b.weight || 0),
      render: (weight) => <Text strong>{weight} kg</Text>,
    },
    {
      title: "Gain (kg)",
      key: "gain",
      width: 100,
      render: (_, record, index) => {
        // Most recent record (index 0) has no later measurement to compare to
        if (index === 0) return "-";

        const nextWeight = animalHistory[index - 1]?.weight; // more recent record
        const gain =
          nextWeight != null ? +(nextWeight - record.weight).toFixed(2) : 0;

        const color = gain > 0 ? "#52c41a" : gain < 0 ? "#ff4d4f" : undefined;
        const sign = gain > 0 ? "+" : "";

        return (
          <Text style={{ color }}>
            {sign}
            {gain}
          </Text>
        );
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
  ];

  // Filtered animals based on search
  const getFilteredAnimals = (type) => {
    const animals = animalsData[type] || [];
    if (!searchTerm) return animals;
    return animals.filter((animal) =>
      animal.tagNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const currentAnimals = getFilteredAnimals(activeTab);
  const currentConfig = animalTypes[activeTab];

  return (
    <>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Animals"
              value={stats.totalAnimals}
              prefix={<TeamOutlined />}
              // valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Average Weight"
              value={stats.avgWeight}
              suffix="kg"
              precision={1}
              prefix={<RiseOutlined />}
              // valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Animals"
              value={currentAnimals.filter((a) => a.status === "alive").length}
              prefix={<CheckCircleOutlined />}
              // valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ready for Market"
              value={currentAnimals.filter((a) => a.marketReadiness === "Ready FOR MARKET").length}
              prefix={<WarningOutlined />}
              suffix="animals"
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title={
          <Space>
            <span>Animal Growth Tracker</span>
            <Badge
              count={currentAnimals.length}
              showZero
              color={currentConfig?.color}
            />
          </Space>
        }
        extra={
          <Space>
            <Input.Search
              placeholder="Search by tag number..."
              allowClear
              style={{ width: 250 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              Add Weight Record
            </Button>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setThresholdModalOpen(true)}
            >
              Manage Thresholds
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchAnimals}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
          {Object.entries(animalTypes).map(([key, config]) => (
            <TabPane
              tab={
                <Space>
                  <span>{config.icon}</span>
                  <span>{config.name}</span>
                  <Badge
                    count={animalsData[key]?.length || 0}
                    showZero
                    color={config.color}
                  />
                </Space>
              }
              key={key}
            >
              <Table
                columns={getColumns(key)}
                dataSource={currentAnimals}
                rowKey="tagNumber"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} animals`,
                }}
                scroll={{ x: 800 }}
                size="small"
              />
            </TabPane>
          ))}
        </Tabs>
      </Card>

      <ThresholdModal
        open={thresholdModalOpen}
        onClose={() => setThresholdModalOpen(false)}
        activeTab={activeTab}
      />

      {/* Add Weight Modal */}
      <Modal
        title="Add Weight Record"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={handleAddWeight}>
          <Form.Item
            name="tagNumber"
            label="Select Animal"
            rules={[{ required: true, message: "Please select an animal" }]}
          >
            <Select
              showSearch
              placeholder="Search by tag number"
              optionFilterProp="label"
              loading={loadingTags}
              options={availableTags}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name="weight"
            label="Weight (kg)"
            rules={[{ required: true, message: "Please enter weight" }]}
          >
            <InputNumber
              min={0}
              step={0.1}
              style={{ width: "100%" }}
              placeholder="Enter weight in kg"
            />
          </Form.Item>

          <Form.Item
            name="measurementDate"
            label="Measurement Date"
            rules={[{ required: true }]}
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Additional notes..." />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Save Record
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Growth History Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>Growth History</span>
            <Tag color={animalTypes[selectedAnimal?.type]?.color}>
              {selectedAnimal?.tagNumber}
            </Tag>
          </Space>
        }
        open={historyModalVisible}
        onCancel={() => {
          setHistoryModalVisible(false);
          setSelectedAnimal(null);
          setAnimalHistory([]);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setHistoryModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedAnimal && (
          <>
            <Descriptions
              size="small"
              bordered
              column={2}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Tag Number">
                <Tag color={animalTypes[selectedAnimal.type]?.color}>
                  {selectedAnimal.tagNumber}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                {animalTypes[selectedAnimal.type]?.name}
              </Descriptions.Item>
              <Descriptions.Item label="Initial Weight">
                <Text strong>{selectedAnimal.initialWeight || 0} kg</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Current Weight">
                <Text strong>{selectedAnimal.currentWeight || 0} kg</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Last Measured">
                {selectedAnimal.lastMeasurement
                  ? dayjs(selectedAnimal.lastMeasurement).format("DD MMM YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Farm" span={2}>
                {selectedAnimal.farm || "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <Table
              columns={historyColumns}
              dataSource={animalHistory}
              rowKey="measurement_date"
              pagination={{ pageSize: 10 }}
              size="small"
              loading={loading}
            />

            {animalHistory.length > 1 && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: "#f6ffed",
                  borderRadius: 8,
                }}
              >
                <Text strong>Summary:</Text>
                <div style={{ marginTop: 8 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Current Weight"
                        value={
                          animalHistory[animalHistory.length - 1]?.weight || 0
                        }
                        suffix="kg"
                        size="small"
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Initial Weight"
                        value={animalHistory[0]?.weight || 0}
                        suffix="kg"
                        size="small"
                      />
                    </Col>
                    <Col span={8}>
                      {(() => {
                        const latestWeight = animalHistory[0]?.weight || 0;
                        const earliestWeight =
                          animalHistory[animalHistory.length - 1]?.weight || 0;
                        const totalGain = +(
                          latestWeight - earliestWeight
                        ).toFixed(2);
                        const color =
                          totalGain > 0
                            ? "#52c41a"
                            : totalGain < 0
                            ? "#ff4d4f"
                            : undefined;
                        const prefix = totalGain > 0 ? "+" : "";

                        return (
                          <Statistic
                            title="Total Gain"
                            value={`${prefix}${totalGain}`}
                            suffix="kg"
                            valueStyle={{ color }}
                            size="small"
                          />
                        );
                      })()}
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default AnimalGrowthTracker;
